<template>
  <div>
    <el-row class="margin-top">
      <el-col :span="24">
        <!-- 批量操作栏 -->
        <el-row v-if="selectedItems.length > 0" class="batch-toolbar margin-bottom" style="margin-bottom: 15px;">
          <el-col :span="24">
            <el-alert
              :title="`已选择 ${selectedItems.length} 条记录，合计实付金额 ${selectedTotalAmount} 元`"
              type="info"
              :closable="false"
              show-icon
            >
              <template slot="default">
                <el-button
                  type="primary"
                  size="mini"
                  style="margin-left: 20px;"
                  @click="openBatchReturnPayFeeDialog"
                >
                  批量申请退费
                </el-button>
                <el-button size="mini" @click="clearSelection">取消选择</el-button>
              </template>
            </el-alert>
          </el-col>
        </el-row>

        <!-- 2026-07-06 新增：缴费历史检索条件（状态、收银员、费用项、缴费时间） -->
        <el-row :gutter="20" class="filter-container" style="margin-bottom: 15px;">
          <el-col :span="5">
            <el-date-picker
              v-model="queryParams.startTime"
              type="date"
              placeholder="开始时间"
              value-format="yyyy-MM-dd"
              style="width: 100%;"
            />
          </el-col>
          <el-col :span="5">
            <el-date-picker
              v-model="queryParams.endTime"
              type="date"
              placeholder="结束时间"
              value-format="yyyy-MM-dd"
              style="width: 100%;"
            />
          </el-col>
          <el-col :span="4">
            <el-select v-model="queryParams.state" placeholder="状态" clearable style="width: 100%;">
              <el-option
                v-for="item in queryParams.states"
                :key="item.statusCd"
                :label="item.name"
                :value="item.statusCd"
              />
            </el-select>
          </el-col>
          <el-col :span="5">
            <el-input v-model="queryParams.cashierName" placeholder="收银员" clearable />
          </el-col>
          <el-col :span="5">
            <el-select v-model="queryParams.configId" placeholder="费用项" clearable style="width: 100%;">
              <el-option
                v-for="item in queryParams.feeConfigs"
                :key="item.configId"
                :label="item.feeName"
                :value="item.configId"
              />
            </el-select>
          </el-col>
        </el-row>
        <el-row :gutter="20" class="filter-container" style="margin-bottom: 15px;">
          <el-col :span="24" style="text-align: right;">
            <el-button type="primary" size="small" @click="_querySimplifyHisFee">
              <i class="el-icon-search"></i> 查询
            </el-button>
            <el-button size="small" @click="_resetSimplifyHisFee">
              <i class="el-icon-refresh"></i> 重置
            </el-button>
          </el-col>
        </el-row>

        <el-table
          ref="feeTable"
          :data="simplifyHisFeeInfo.feeDetails"
          style="width: 100%; margin-top: 10px"
          border
          stripe
          v-loading="loading"
          row-key="detailId"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="55" :selectable="isSelectable" reserve-selection />
          <el-table-column prop="feeName" :label="$t('simplifyHisFee.feeItem')" align="center"></el-table-column>
          <el-table-column prop="payerObjName" :label="$t('simplifyHisFee.payerObject')" align="center"></el-table-column>
          <el-table-column :label="$t('simplifyHisFee.receiptNumber')" align="center">
            <template slot-scope="scope">
              <div v-if="scope.row.receiptCode">{{ scope.row.receiptCode }}</div>
              <div v-else>
                <el-link
                  v-if="scope.row.state === '1400'"
                  type="primary"
                  @click="_openGeneratorReceiptCode(scope.row)"
                >
                  {{ $t('simplifyHisFee.manualGenerate') }}
                </el-link>
                <span v-else>-</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="cycles" :label="$t('simplifyHisFee.cycle')" align="center"></el-table-column>
          <el-table-column :label="$t('simplifyHisFee.receivableReceived')" align="center">
            <template slot-scope="scope">
              <div v-if="scope.row.state === '1500'">
                {{ scope.row.payerObjName }}{{ $t('simplifyHisFee.viewInFee') }}
              </div>
              <div v-else>
                {{ scope.row.receivableAmount }}/{{ scope.row.receivedAmount }}<br>
                <div v-if="scope.row.acctAmount > 0">
                  {{ $t('simplifyHisFee.accountDeduction') }}: {{ scope.row.acctAmount }}<br>
                </div>
                <div
                  v-for="(item, index) in scope.row.payFeeDetailDiscountDtoList"
                  :key="index"
                >
                  {{ item.discountName }}: {{ Math.abs(item.discountPrice) }}<br>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="primeRateName" :label="$t('simplifyHisFee.paymentMethod')" align="center"></el-table-column>
          <el-table-column :label="$t('simplifyHisFee.paymentPeriod')" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.startTime) }}~<br>
              {{ formatDate(scope.row.endTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="createTime" :label="$t('simplifyHisFee.paymentTime')" align="center"></el-table-column>
          <el-table-column prop="cashierName" :label="$t('simplifyHisFee.cashier')" align="center">
            <template slot-scope="scope">
              {{ scope.row.cashierName || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="stateName" :label="$t('simplifyHisFee.status')" align="center"></el-table-column>
          <el-table-column prop="remark" :label="$t('simplifyHisFee.remark')" align="center"></el-table-column>
          <el-table-column :label="$t('simplifyHisFee.operation')" align="center">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                plain
                @click="_toHisFeeDetail(scope.row)"
              >
                {{ $t('simplifyHisFee.detail') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-size="pageSize"
          layout="total, prev, pager, next"
          :total="simplifyHisFeeInfo.total"
          class="pagination-container"
        ></el-pagination>
      </el-col>
    </el-row>

    <!-- 批量退费弹窗 -->
    <batch-return-pay-fee-dialog
      ref="batchReturnPayFeeDialog"
      @confirm="handleBatchReturnPayFeeConfirm"
    />

    <!-- 批量退费进度弹窗 -->
    <batch-return-pay-fee-progress
      ref="batchReturnPayFeeProgress"
      @complete="handleBatchReturnPayFeeComplete"
    />

    <el-dialog
      :title="$t('simplifyHisFee.generateReceipt')"
      :visible.sync="receiptDialogVisible"
      width="50%"
    >
      <el-form label-position="left" label-width="120px">
        <el-form-item :label="$t('simplifyHisFee.receiptNumber')">
          <el-select
            v-model="simplifyHisFeeInfo.receiptType"
            class="w-100"
            :placeholder="$t('simplifyHisFee.receiptNumberRequired')"
          >
            <el-option disabled value="">{{ $t('simplifyHisFee.receiptNumberRequired') }}</el-option>
            <el-option value="Y" :label="$t('simplifyHisFee.systemGenerate')"></el-option>
            <el-option value="N" :label="$t('simplifyHisFee.custom')"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="simplifyHisFeeInfo.receiptType === 'N'"
          :label="$t('simplifyHisFee.receiptNumber')"
        >
          <el-input
            v-model="simplifyHisFeeInfo.receiptCode"
            :placeholder="$t('simplifyHisFee.receiptNumberRequired')"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="receiptDialogVisible = false">{{ $t('simplifyHisFee.cancel') }}</el-button>
        <el-button type="primary" @click="_generatorReceiptCode">{{ $t('simplifyHisFee.generate') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { queryFeeDetail, generatorReceipt } from '@/api/fee/simplifyHisFeeApi'
import { getCommunityId, getDict } from '@/api/community/communityApi'
import { listFeeConfigs } from '@/api/fee/propertyFeeApi'
import { dateFormat } from '@/utils/dateUtil'
import BatchReturnPayFeeDialog from './batchReturnPayFeeDialog'
import BatchReturnPayFeeProgress from './batchReturnPayFeeProgress'

export default {
  name: 'SimplifyHisFee',

  components: {
    BatchReturnPayFeeDialog,
    BatchReturnPayFeeProgress
  },
  data() {
    return {
      DEFAULT_PAGE: 1,
      DEFAULT_ROWS: 200,
      currentPage: 1,
      pageSize: 200,
      receiptDialogVisible: false,
      loading: false,
      selectedItems: [],       // 当前选中的记录
      selectedTotalAmount: 0,   // 选中记录的合计实付金额
      simplifyHisFeeInfo: {
        total: 0,
        records: 1,
        feeDetails: [],
        ownerId: '',
        feeDetail: {},
        receiptType: 'Y',
        receiptCode: ''
      },
      // 2026-07-06 新增：缴费历史检索条件
      queryParams: {
        startTime: '',
        endTime: '',
        state: '',
        cashierName: '',
        configId: '',
        states: [],
        feeConfigs: []
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    // 2026-07-06 新增：加载状态下拉和费用项下拉
    this._loadDictData()
    this._loadFeeConfigs()
  },
  methods: {
    open(param) {
      this.handleSwitch(param)
    },
    handleSwitch(param) {
      this.clearSimplifyHisFeeInfo()
      if (!param.ownerId) {
        return
      }
      Object.assign(this.simplifyHisFeeInfo, param)
      this._listSimplifyFeeDetails(this.DEFAULT_PAGE, this.DEFAULT_ROWS)
    },
    handleNotify() {
      this._listSimplifyFeeDetails(this.DEFAULT_PAGE, this.DEFAULT_ROWS)
    },
    handleCurrentChange(page) {
      this._listSimplifyFeeDetails(page, this.DEFAULT_ROWS)
    },
    formatDate(date) {
      if (!date) return ''
      return dateFormat(date)
    },
    _listSimplifyFeeDetails(page, row) {
      this.loading = true
      const params = {
        page,
        row,
        communityId: this.communityId,
        ownerId: this.simplifyHisFeeInfo.ownerId,
        // 2026-07-06 新增：传入检索条件
        startTime: this.queryParams.startTime,
        endTime: this.queryParams.endTime,
        state: this.queryParams.state,
        cashierName: this.queryParams.cashierName,
        configId: this.queryParams.configId,
        orderBy: 'createTime'
      }

      queryFeeDetail(params)
        .then(response => {
          const res = response.data
          this.simplifyHisFeeInfo.total = res.total
          this.simplifyHisFeeInfo.records = res.records
          this.simplifyHisFeeInfo.feeDetails = res.feeDetails
          this.currentPage = page
        })
        .catch(error => {
          console.error('请求失败处理', error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    clearSimplifyHisFeeInfo() {
      this.simplifyHisFeeInfo = {
        total: 0,
        records: 1,
        feeDetails: [],
        ownerId: '',
        feeDetail: {},
        receiptType: 'Y',
        receiptCode: ''
      }
      // 2026-07-06 新增：切换房间时清空检索条件，但保留下拉选项数据
      this.queryParams.startTime = ''
      this.queryParams.endTime = ''
      this.queryParams.state = ''
      this.queryParams.cashierName = ''
      this.queryParams.configId = ''
    },
    // 2026-07-06 新增：加载状态下拉框数据
    async _loadDictData() {
      try {
        this.queryParams.states = await getDict('pay_fee_detail', 'state')
      } catch (error) {
        console.error('加载状态字典失败:', error)
      }
    },
    // 2026-07-06 新增：加载费用项下拉数据
    async _loadFeeConfigs() {
      try {
        const res = await listFeeConfigs({
          page: 1,
          row: 500,
          communityId: this.communityId,
          isDefault: 'F'
        })
        this.queryParams.feeConfigs = res.feeConfigs || []
      } catch (error) {
        console.error('加载费用项失败:', error)
      }
    },
    // 2026-07-06 新增：查询缴费历史
    _querySimplifyHisFee() {
      this.currentPage = 1
      this._listSimplifyFeeDetails(this.DEFAULT_PAGE, this.DEFAULT_ROWS)
    },
    // 2026-07-06 新增：重置检索条件
    _resetSimplifyHisFee() {
      this.queryParams.startTime = ''
      this.queryParams.endTime = ''
      this.queryParams.state = ''
      this.queryParams.cashierName = ''
      this.queryParams.configId = ''
      this._querySimplifyHisFee()
    },
    _toHisFeeDetail(detail) {
      this.$router.push(`/views/fee/propertyFee?feeId=${detail.feeId}`)
    },
    _openGeneratorReceiptCode(detail) {
      this.simplifyHisFeeInfo.feeDetail = detail
      this.receiptDialogVisible = true
    },
    _generatorReceiptCode() {
      const data = {
        detailId: this.simplifyHisFeeInfo.feeDetail.detailId,
        communityId: this.communityId,
        receiptCode: this.simplifyHisFeeInfo.receiptCode
      }

      generatorReceipt(data)
        .then(response => {
          const res = response.data
          this.$message.success(res.msg)
          if (res.code !== '0') {
            return
          }
          this.receiptDialogVisible = false
          setTimeout(() => {
            this.$emit('doSearch')
          }, 1000)
        })
        .catch(error => {
          console.error('请求失败处理', error)
          this.$message.error(error.message || '操作失败')
        })
    },

    /**
     * 判断记录是否可选择（用于批量退费）
     * 只有状态为 1400（正常/已缴费）的记录才允许选择
     */
    isSelectable(row) {
      return row.state === '1400'
    },

    /**
     * 选择项变化时触发
     */
    handleSelectionChange(selection) {
      this.selectedItems = selection
      // 计算合计实付金额
      let total = 0
      selection.forEach(item => {
        const amount = parseFloat(item.receivedAmount) || 0
        total += amount
      })
      this.selectedTotalAmount = total.toFixed(2)
    },

    /**
     * 打开批量退费弹窗
     */
    openBatchReturnPayFeeDialog() {
      if (this.selectedItems.length === 0) {
        this.$message.warning('请先选择要退费的记录')
        return
      }
      this.$refs.batchReturnPayFeeDialog.open(this.selectedItems)
    },

    /**
     * 清空选择
     */
    clearSelection() {
      this.selectedItems = []
      this.selectedTotalAmount = 0
      // 清空表格选择状态
      const tableRef = this.$refs.feeTable
      if (tableRef) {
        tableRef.clearSelection()
      }
    },

    /**
     * 批量退费弹窗确认
     */
    handleBatchReturnPayFeeConfirm({ items, reason }) {
      this.$refs.batchReturnPayFeeProgress.open(items, reason)
    },

    /**
     * 批量退费进度完成
     */
    handleBatchReturnPayFeeComplete({ successCount, failCount }) {
      // 清空选择
      this.clearSelection()
      // 刷新列表
      this._listSimplifyFeeDetails(this.currentPage, this.DEFAULT_ROWS)

      if (failCount === 0) {
        this.$message.success(`批量申请退费成功，共 ${successCount} 条记录已提交审核`)
      } else {
        this.$message.warning(`批量申请退费完成，成功 ${successCount} 条，失败 ${failCount} 条`)
      }
    }
  }
}
</script>

<style scoped>
.margin-top {
  margin-top: 20px;
}
.w-100 {
  width: 100%;
}
.pagination-container {
  margin-top: 20px;
  text-align: right;
}
.batch-toolbar {
  margin-bottom: 10px;
}
</style>